<template>
  <div class="tax-summary-shell">
    <div class="d-flex flex-wrap align-center justify-space-between mb-4">
      <div class="pr-3">
        <div class="text-subtitle-1 font-weight-bold text-uppercase">Resumen de declaración de impuestos</div>
        <div class="text-caption grey--text text--darken-1">
          {{ businessLabel }}
        </div>
      </div>

      <div class="d-flex align-center">
        <v-chip
          x-small
          :color="recordState.color"
          text-color="white"
          class="mr-2"
        >
          {{ recordState.label }}
        </v-chip>
        <v-btn icon small @click="loadRecords" :loading="loading" :disabled="!resolvedBusinessId">
          <v-icon color="primary">mdi-reload</v-icon>
        </v-btn>
      </div>
    </div>

    <v-row dense no-gutters class="mb-4">
      <v-col cols="12" sm="6" md="3" class="px-2 mb-2 mb-md-0">
        <v-select
          v-model="selectedMonth"
          :items="monthOptions"
          item-text="text"
          item-value="value"
          label="Mes"
          outlined
          dense
          hide-details
        />
      </v-col>
      <v-col cols="12" sm="6" md="3" class="px-2 mb-2 mb-md-0">
        <div class="d-flex flex-column flex-md-row align-stretch align-md-center">
          <v-select
            v-model="selectedYear"
            :items="yearOptions"
            label="Año"
            outlined
            dense
            hide-details
            class="flex-grow-1"
          />
          <v-btn
            small
            outlined
            color="primary"
            class="mt-2 mt-md-0 ml-md-3 tax-summary-search-btn"
            :loading="loading"
            :disabled="!resolvedBusinessId"
            @click="loadRecords(true)"
          >
            <v-icon left small>mdi-magnify</v-icon>
            Buscar
          </v-btn>
        </div>
      </v-col>
      <v-col cols="12" md="6" class="d-flex align-end justify-end">
        <div class="text-caption grey--text text--darken-1 text-right">
          {{ periodLabel }}
        </div>
      </v-col>
    </v-row>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      height="3"
      class="my-4"
    />

    <template v-else>
      <div v-if="!selectedRecord" class="tax-summary-empty">
        <div class="text-body-2 text-center mb-4">
          Aún no se ha generado la declaración para este periodo.
        </div>
        <v-btn color="primary" outlined small @click="openTaxDeclarationModule">
          <v-icon left small>mdi-open-in-new</v-icon>
          Generar declaración
        </v-btn>
      </div>

      <v-row v-else dense no-gutters class="tax-summary-grid">
        <v-col cols="12" sm="6" md="4" class="px-2 mb-3">
          <div class="tax-summary-card">
            <div class="tax-summary-card-head">Débito fiscal</div>
            <div class="tax-summary-card-value">{{ formatMoney(selectedRecord.DebitoFiscal) }}</div>
            <div class="tax-summary-card-caption">Ventas totales {{ formatMoney(selectedRecord.TotalVentas) }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" class="px-2 mb-3">
          <div class="tax-summary-card">
            <div class="tax-summary-card-head">Crédito fiscal</div>
            <div class="tax-summary-card-value">{{ formatMoney(selectedRecord.CreditoFiscal) }}</div>
            <div class="tax-summary-card-caption">Compras totales {{ formatMoney(selectedRecord.TotalCompras) }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" class="px-2 mb-3">
          <div class="tax-summary-card">
            <div class="tax-summary-card-head">Diferencia de IVA</div>
            <div class="tax-summary-card-value">{{ formatMoney(selectedRecord.DiferenciaIVA) }}</div>
            <div class="tax-summary-card-caption">Remanente del periodo anterior {{ formatMoney(selectedRecord.RemanentePeriodoAnterior) }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" class="px-2 mb-3">
          <div class="tax-summary-card">
            <div class="tax-summary-card-head">IVA por pagar / remanente</div>
            <div class="tax-summary-card-value">{{ formatMoney(selectedRecord.SaldoPendiente) }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" class="px-2 mb-3">
          <div class="tax-summary-card">
            <div class="tax-summary-card-head">Pago a cuenta calculado</div>
            <div class="tax-summary-card-value">{{ formatMoney(selectedRecord.PagoCuentaRemanenteSiguientePeriodo) }}</div>
            <div class="tax-summary-card-caption">Ingresos sin retención pago a cuenta {{ formatMoney(selectedRecord.PagoCuentaIngresosSinRetencion) }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" class="px-2 mb-3">
          <div class="tax-summary-card">
            <div class="tax-summary-card-head">Renta</div>
            <div class="tax-summary-card-value">{{ formatMoney(selectedRecord.RentaTotalAPagarRetenciones) }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" class="px-2 mb-3">
          <div class="tax-summary-card">
            <div class="tax-summary-card-head">Mandamiento de pagos</div>
            <div class="tax-summary-card-value">{{ formatMoney(selectedRecord.TotalAPagarMandamientos) }}</div>
          </div>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
export default {
  name: "TaxDeclarationSummary",
  props: {
    businessId: {
      type: [Number, String],
      default: null,
    },
    refreshToken: {
      type: Number,
      default: 0,
    },
    businessLabel: {
      type: String,
      default: "--",
    },
    autoLoad: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const now = new Date();
    return {
      loading: false,
      records: [],
      selectedMonth: now.getMonth() + 1,
      selectedYear: now.getFullYear(),
      monthOptions: [
        { text: "Enero", value: 1 },
        { text: "Febrero", value: 2 },
        { text: "Marzo", value: 3 },
        { text: "Abril", value: 4 },
        { text: "Mayo", value: 5 },
        { text: "Junio", value: 6 },
        { text: "Julio", value: 7 },
        { text: "Agosto", value: 8 },
        { text: "Septiembre", value: 9 },
        { text: "Octubre", value: 10 },
        { text: "Noviembre", value: 11 },
        { text: "Diciembre", value: 12 },
      ],
    };
  },
  computed: {
    resolvedBusinessId() {
      return this.businessId !== null && this.businessId !== undefined ? Number(this.businessId) : null;
    },
    selectedRecord() {
      const periodRecords = this.records.filter((item) => {
        return Number(item.PeriodMonth) === Number(this.selectedMonth) &&
          Number(item.PeriodYear) === Number(this.selectedYear);
      });

      if (!periodRecords.length) {
        return null;
      }
      return periodRecords[0];
    },
    periodLabel() {
      const month = this.monthOptions.find((item) => item.value === Number(this.selectedMonth))?.text || "";
      return `${month} ${this.selectedYear}`;
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const yearSet = new Set([currentYear, ...Array.from({ length: 10 }, (_, index) => currentYear - index)]);

      this.records.forEach((record) => {
        const year = Number(record.PeriodYear);
        if (year) {
          yearSet.add(year);
        }
      });

      return Array.from(yearSet).sort((a, b) => b - a);
    },
    recordState() {
      if (!this.resolvedBusinessId) {
        return { label: "Negocio requerido", color: "grey" };
      }

      if (!this.selectedRecord) {
        return { label: "Sin declaración", color: "error" };
      }

      return Number(this.selectedRecord.Status) === 1
        ? { label: "Procesada", color: "success" }
        : { label: "Pendiente", color: "warning" };
    },
  },
  watch: {
    businessId: {
      immediate: true,
      handler() {
        if (this.autoLoad) {
          this.loadRecords();
        }
      },
    },
    refreshToken() {
      if (this.autoLoad) {
        this.loadRecords();
      }
    },
  },
  methods: {
    openTaxDeclarationModule() {
      this.$store.dispatch("addWindow", {
        name: "AccountingSalesPurchases",
        component: "AccountingSalesPurchases",
        unique: true,
        meta: {
          tab: "tab-3",
        },
      });
    },
    loadRecords(forceRefresh = false) {
      if (!this.resolvedBusinessId) {
        this.records = [];
        return Promise.resolve();
      }

      if (forceRefresh) {
        this.records = [];
      }

      this.loading = true;
      return this.$API.accountingtotalsrecord
        .list({
          ID_Business: this.resolvedBusinessId,
          _ts: Date.now(),
        })
        .then((response) => {
          this.records = response?.data?.data || response?.data || [];
        })
        .catch((error) => {
          console.warn("Error loading tax declaration summary", error);
          this.records = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    toNumber(value) {
      if (value === null || value === undefined || value === "") {
        return 0;
      }

      if (typeof value === "number") {
        return Number.isFinite(value) ? value : 0;
      }

      const normalized = String(value)
        .replace(/,/g, "")
        .replace(/[^\d.-]/g, "");

      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    formatMoney(value) {
      const amount = this.toNumber(value);
      return `$${amount.toFixed(2)}`;
    },
  },
};
</script>

<style scoped>
.tax-summary-shell {
  width: 100%;
  background-color: #ffffff;
  border: 1px solid #000000;
  border-radius: 15px;
  padding: 0.9rem;
  margin-bottom: 1.5rem;
  box-sizing: border-box;
  overflow-x: hidden;
}

.tax-summary-empty {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.5rem 0;
}

.tax-summary-grid {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.tax-summary-card {
  border: 1px solid #d8e2ea;
  border-radius: 8px;
  background: #fff;
  padding: 0.85rem 1rem;
  min-height: 100%;
}

.tax-summary-card-head {
  font-size: 11px;
  text-transform: uppercase;
  color: #5f6b76;
  margin-bottom: 0.4rem;
  letter-spacing: 0;
}

.tax-summary-card-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2937;
}

.tax-summary-card-caption {
  margin-top: 0.35rem;
  font-size: 0.8rem;
  color: #607080;
  line-height: 1.2;
}

.tax-summary-search-btn {
  min-width: 108px;
  height: 40px;
  border-radius: 8px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  background-color: #59a9dd !important;
  border-color: #59a9dd !important;
  color: #ffffff !important;
}

.tax-summary-search-btn ::v-deep .v-icon {
  color: #ffffff !important;
}
</style>
